import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Heading,
    SimpleGrid,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {
    useCategory,
    useCustomer,
    useCustomerId,
    useCustomerType
} from '@salesforce/commerce-sdk-react'
import {
    PRODUCT_LIST_IMAGE_VIEW_TYPE,
    PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'
import {useBrand} from '../../contexts/brand-context'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import {useFypLandingProducts} from '../../hooks/use-fyp-landing-products'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import {renderLayoutsSwitch} from '../../components/optimizely/utils/contentRenderer'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import GridViewSelector, {GRID_VIEW} from '../product-list/partials/grid-view-selector'
import ItemsCounter from '../product-list/partials/items-counter'
import PageNotFound from '../page-not-found'
import {CAT_MENU_DEFAULT_NAV_SSR_DEPTH, FOR_YOU_CATEGORY_LOOKUP_DEPTH} from '../../constants'
import {getRootCategoryId} from '../../utils/brandUtils'
import {getFypMaxProductCount, getFypPageSize, isForYouEnabled} from '../../utils/fyp-flags'
import {findForYouCategory, getForYouName} from '../../utils/fyp-utils'
import {
    PLP_ADJACENT_AREA,
    extractBlocksForCategory
} from '../product-list/utils/extract-plp-area-blocks'

const SKELETON_TILES = 8

// Same cookie the PLP and UGC landing write, so the grid choice follows the shopper.
const GRID_VIEW_COOKIE = 'plpGridView'
const readSavedGridView = () => {
    if (typeof window === 'undefined') return null
    const match = document.cookie.match(/(?:^|;\s*)plpGridView=([^;]+)/)
    return (match ? decodeURIComponent(match[1]) : null) || localStorage.getItem(GRID_VIEW_COOKIE)
}

const ForYou = () => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('ForYouPage', {})
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const {brandId} = useBrand()
    const siteId = site?.id

    const isEnabled = isForYouEnabled(appConfig, siteId, brandId)
    const pageSize = getFypPageSize(appConfig, siteId)
    const maxProducts = getFypMaxProductCount(appConfig, siteId)

    const customerId = useCustomerId()
    const {isRegistered} = useCustomerType()
    const {data: customer} = useCustomer(
        {parameters: {customerId}},
        {enabled: isRegistered && Boolean(customerId)}
    )
    const authModal = useAuthModal()

    const [gridView, setGridView] = useState(GRID_VIEW.DEFAULT)
    const isLargeGridView = gridView === GRID_VIEW.ALTERNATIVE
    const tileResponsiveDisplay = isLargeGridView
        ? {onImageBadge: {display: 'none'}}
        : undefined

    // Render DEFAULT first (matches SSR), then apply the saved choice after hydration.
    useEffect(() => {
        const saved = readSavedGridView()
        if (saved && saved !== gridView) setGridView(saved)
    }, [])

    const setGridViewPersisted = (view) => {
        setGridView(view)
        if (typeof document !== 'undefined') {
            document.cookie = `${GRID_VIEW_COOKIE}=${view}; path=/; max-age=31536000; samesite=lax`
        }
        if (typeof window !== 'undefined') window.localStorage.setItem(GRID_VIEW_COOKIE, view)
    }

    // ECOM flags one nav category (main or child) with c_isForYou; look deeper only
    // when the shallow nav tree misses it.
    const rootCategoryId = getRootCategoryId(siteId, brandId)
    const {data: navCategoriesTree, isLoading: isNavTreeLoading} = useCategory({
        parameters: {id: rootCategoryId, levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH},
        enabled: isEnabled && Boolean(rootCategoryId)
    })
    const navForYouCategory = useMemo(
        () => findForYouCategory(navCategoriesTree),
        [navCategoriesTree]
    )
    const shouldLookDeeper =
        isEnabled && Boolean(rootCategoryId) && !isNavTreeLoading && !navForYouCategory
    const {data: deepCategoriesTree, isLoading: isDeepLoading} = useCategory({
        parameters: {id: rootCategoryId, levels: FOR_YOU_CATEGORY_LOOKUP_DEPTH},
        enabled: shouldLookDeeper
    })
    const forYouCategory = useMemo(
        () => navForYouCategory || findForYouCategory(deepCategoriesTree),
        [navForYouCategory, deepCategoriesTree]
    )
    const categoryId = forYouCategory?.id

    // `shouldLookDeeper` stays true after the deeper query answers, so track that it
    // settled before treating a missing category as final.
    const [isDeepSettled, setIsDeepSettled] = useState(false)
    useEffect(() => {
        if (!shouldLookDeeper || isDeepLoading) return
        if (deepCategoriesTree) setIsDeepSettled(true)
    }, [shouldLookDeeper, isDeepLoading, deepCategoriesTree])

    const isCategoryResolving =
        isEnabled && (isNavTreeLoading || (shouldLookDeeper && !isDeepSettled))

    const {
        products,
        total,
        isLoading,
        isLoadingMore,
        isLoadingPrevious,
        isFailed,
        hasMore,
        showLoadPrevious,
        showMore,
        loadPrevious
    } = useFypLandingProducts({categoryId, pageSize, maxProducts})
    const hasProducts = products.length > 0

    const displayName = getForYouName(customer?.firstName)
    // Title case in code; Garage uppercases it in the theme.
    const title = displayName
        ? intl.formatMessage(
              {id: 'for_you.title.signed_in', defaultMessage: 'For {firstName}'},
              {firstName: displayName}
          )
        : intl.formatMessage({id: 'for_you.title.guest', defaultMessage: 'For You'})

    // The banner is a CMS text block on the shared PLP page, targeted at the For You
    // category. Per-tier copy is CMS visibility control, already applied by
    // useOptimizelyPage, so this only renders what survived the filter.
    const {data: plpContentPage} = useOptimizelyPage('plp')
    const plpPage = plpContentPage?.item || plpContentPage?.page || plpContentPage
    const bannerBlocks = useMemo(
        () =>
            extractBlocksForCategory(plpPage?.AboveProductListArea, {
                ...PLP_ADJACENT_AREA,
                categoryIds: [categoryId]
            }),
        [plpPage, categoryId]
    )

    if (!isEnabled || (!isCategoryResolving && !categoryId)) {
        return <PageNotFound />
    }

    return (
        <Box {...styles.container} data-testid="for-you-page">
            <Heading as="h1" {...styles.title} data-testid="for-you-title">
                {title}
            </Heading>

            {hasProducts && bannerBlocks.length > 0 && (
                <Box {...styles.subtitle} data-testid="for-you-banner">
                    {bannerBlocks.map((block, index) => (
                        <Box key={`for-you-banner-${block?.ContentLink?.GuidValue ?? index}`}>
                            {renderLayoutsSwitch(block)}
                        </Box>
                    ))}
                </Box>
            )}

            {!hasProducts && !isFailed && (
                <Text {...styles.subtitle} data-testid="for-you-empty-copy">
                    {intl.formatMessage({
                        id: 'for_you.empty',
                        defaultMessage:
                            "We're refreshing your picks. In the meantime, explore what's new."
                    })}
                </Text>
            )}

            <Box {...styles.toolbar}>
                <Box {...styles.toolbarLeft}>
                    {!isLoading && total > 0 && (
                        <Text {...styles.itemCount} data-testid="for-you-item-count">
                            {intl.formatMessage(
                                {
                                    id: 'for_you.item_count',
                                    defaultMessage: '{count, plural, one {# item} other {# items}}'
                                },
                                {count: total}
                            )}
                        </Text>
                    )}
                </Box>
                <Box {...styles.toolbarCenter}>
                    <Box display={{base: 'none', md: 'block'}}>
                        <GridViewSelector gridView={gridView} setGridView={setGridViewPersisted} />
                    </Box>
                    <Box display={{base: 'block', md: 'none'}}>
                        <GridViewSelector gridView={gridView} setGridView={setGridViewPersisted} />
                    </Box>
                </Box>
            </Box>

            {isFailed && (
                <Text {...styles.errorMessage} data-testid="for-you-error-message">
                    {intl.formatMessage({
                        id: 'for_you.error',
                        defaultMessage:
                            "We couldn't load your picks right now. Please try again later."
                    })}
                </Text>
            )}

            {showLoadPrevious && (
                <Flex
                    justifyContent="center"
                    {...styles.showPreviousContainer}
                    data-testid="for-you-show-previous-container"
                >
                    <Button
                        variant="webPrimary"
                        isLoading={isLoadingPrevious}
                        data-testid="for-you-show-previous"
                        onClick={loadPrevious}
                        {...styles.showMoreButton}
                    >
                        {intl.formatMessage({
                            id: 'plp.button.show.previous',
                            defaultMessage: 'Show Previous'
                        })}
                    </Button>
                </Flex>
            )}

            <SimpleGrid
                {...styles.grid}
                role="list"
                columns={isLargeGridView ? [3, 3, 6, 6] : [2, 2, 4, 4]}
            >
                {isLoading
                    ? Array.from({length: SKELETON_TILES}, (_, index) => (
                          <Box key={`for-you-skeleton-${index}`} role="listitem">
                              <ProductTileSkeleton />
                          </Box>
                      ))
                    : products.map((product) => (
                          <Box key={product.id} role="listitem">
                              <ProductTile
                                  product={product}
                                  enableFavourite={!isLargeGridView}
                                  imageViewType={PRODUCT_LIST_IMAGE_VIEW_TYPE}
                                  selectableAttributeId={PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID}
                                  showSwatches={!isLargeGridView}
                                  showDetails={!isLargeGridView}
                                  showQV={!isLargeGridView}
                                  responsiveDisplay={tileResponsiveDisplay}
                                  data-testid="for-you-product-tile"
                              />
                          </Box>
                      ))}
            </SimpleGrid>

            {hasMore && (
                <Flex
                    flexDirection="column"
                    alignItems="center"
                    gap={8}
                    {...styles.showMoreContainer}
                >
                    {products.length < maxProducts && (
                        <ItemsCounter
                            loadedCount={products.length}
                            totalCount={Math.min(total, maxProducts)}
                        />
                    )}
                    <Button
                        variant="webPrimary"
                        isLoading={isLoadingMore}
                        data-testid="for-you-show-more"
                        onClick={showMore}
                        {...styles.showMoreButton}
                    >
                        {intl.formatMessage({
                            id: 'for_you.button.show_more',
                            defaultMessage: 'Show More'
                        })}
                    </Button>
                </Flex>
            )}

            <AuthModal {...authModal} />
        </Box>
    )
}

export default ForYou
